import { useEffect, useRef, useState } from 'react';
import logoSvg from '@/assets/logo-dark.svg?raw';
import { useAuth } from '@/auth/useAuth';

const KEY = 'grhamoy.playIntro';
const DURATION_MS = 3400;
const REDUCED_MS = 900;

/**
 * Order of the big wordmark letters among the logo file's <path>s (0 = clip,
 * 1 = frame, 2–26 = tagline, 34–35 = ™).
 */
const LETTER_PATH = { g: 31, r: 27, h: 32, a: 28, m: 33, o: 29, y: 30 } as const;
type Letter = keyof typeof LETTER_PATH;

/** Where the ball touches down on each letter, in the logo's viewBox units. */
const BALL_R = 9;
const STOPS: { letter: Letter; x: number; y: number }[] = [
  { letter: 'g', x: 113.7, y: 99.4 },
  { letter: 'r', x: 189.7, y: 104.4 },
  { letter: 'h', x: 282.2, y: 106.4 },
  { letter: 'a', x: 350.2, y: 106.4 },
  { letter: 'm', x: 221.2, y: 183.4 },
  { letter: 'o', x: 322.7, y: 183.4 },
  { letter: 'y', x: 356.2, y: 185.4 },
];

const DROP_AT = 520; // ms — ball falls onto the "g"
const DROP_MS = 380;
const HOP_MS = 270;
const ACCENT = '#FF8C00';

function readStart(): number {
  try { return Number(sessionStorage.getItem(KEY) ?? 0) || 0; } catch { return 0; }
}
function writeStart(v: number) {
  try { if (v) sessionStorage.setItem(KEY, String(v)); else sessionStorage.removeItem(KEY); } catch { /* storage blocked */ }
}

const EVENT = 'grhamoy:intro';
const NOTE_KEY = 'grhamoy.introNote';

export type IntroKind = 'welcome' | 'goodbye';
type Note = { kind: IntroKind; name?: string };

function readNote(): Note {
  try { return (JSON.parse(sessionStorage.getItem(NOTE_KEY) ?? 'null') as Note | null) ?? { kind: 'welcome' }; } catch { return { kind: 'welcome' }; }
}
function writeNote(n: Note | null) {
  try { if (n) sessionStorage.setItem(NOTE_KEY, JSON.stringify(n)); else sessionStorage.removeItem(NOTE_KEY); } catch { /* storage blocked */ }
}

/**
 * Play the logo intro now — 'welcome' after sign-in, 'goodbye' on logout. It plays
 * once, over whatever screen follows, with a note under the logo.
 */
export function queueIntro(kind: IntroKind = 'welcome', name?: string) {
  writeNote({ kind, name });
  writeStart(-1);
  window.dispatchEvent(new Event(EVENT));
}

/**
 * LinkedIn-template-style logo intro shown once after sign-in: the wordmark pops
 * in, then an orange ball bounces letter to letter (g → r → h → a → m → o → y),
 * each letter squashing as it lands, and the ball finally swells to fill the
 * screen and fades into the app. Click / tap or any key skips it.
 */
export function IntroSplash() {
  const reduce = typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const duration = reduce ? REDUCED_MS : DURATION_MS;
  const [startedAt, setStartedAt] = useState(() => {
    const s = readStart();
    if (s === -1) { const now = Date.now(); writeStart(now); return now; }
    return s > 0 && Date.now() - s < duration ? s : 0;
  });
  const hostRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const [note, setNote] = useState<Note>(readNote);

  // Started while already on screen (e.g. Logout): pick up the queued intro.
  useEffect(() => {
    const onQueue = () => {
      if (readStart() !== -1) return;
      const now = Date.now();
      writeStart(now);
      setNote(readNote());
      setStartedAt(now);
    };
    window.addEventListener(EVENT, onQueue);
    return () => window.removeEventListener(EVENT, onQueue);
  }, []);

  // End (timer, click or key).
  useEffect(() => {
    if (!startedAt) return;
    const end = () => { writeStart(0); writeNote(null); setStartedAt(0); };
    const t = window.setTimeout(end, Math.max(0, startedAt + duration - Date.now()));
    window.addEventListener('keydown', end);
    return () => { window.clearTimeout(t); window.removeEventListener('keydown', end); };
  }, [startedAt, duration]);

  // Build the animation on the inline SVG.
  useEffect(() => {
    const host = hostRef.current;
    if (!startedAt || !host) return;
    host.innerHTML = logoSvg;
    const svg = host.querySelector('svg');
    if (!svg) return;
    svg.removeAttribute('width');
    svg.removeAttribute('height');
    svg.classList.add('intro-logo-svg');
    const paths = [...svg.querySelectorAll('path')];
    const anims: Animation[] = [];
    const add = (el: Element, frames: Keyframe[], opts: KeyframeAnimationOptions) => anims.push(el.animate(frames, { fill: 'both', ...opts }));

    // Letters and tagline pop in.
    const letters = Object.fromEntries(Object.entries(LETTER_PATH).map(([k, i]) => [k, paths[i]])) as Record<Letter, SVGPathElement | undefined>;
    Object.values(letters).forEach((p, i) => {
      if (!p) return;
      p.style.transformBox = 'fill-box';
      p.style.transformOrigin = '50% 100%';
      add(p, [{ opacity: 0, transform: 'scale(0.4)' }, { opacity: 1, transform: 'scale(1.08)', offset: 0.7 }, { opacity: 1, transform: 'scale(1)' }],
        { duration: 420, delay: 40 + i * 45, easing: 'cubic-bezier(.3,1.4,.5,1)' });
    });
    paths.slice(2, 27).concat(paths.slice(34, 36)).forEach((p) => add(p, [{ opacity: 0 }, { opacity: 1 }], { duration: 400, delay: 380 }));

    if (reduce) return () => anims.forEach((a) => a.cancel());

    // The ball — drawn in the logo's own coordinates, above everything.
    const NS = 'http://www.w3.org/2000/svg';
    // ball (position) › swell (finale, grows from its centre) › dot (squash, from its base)
    const ball = document.createElementNS(NS, 'g');
    const swell = document.createElementNS(NS, 'g');
    const dot = document.createElementNS(NS, 'circle');
    dot.setAttribute('r', String(BALL_R));
    dot.setAttribute('fill', ACCENT);
    swell.appendChild(dot);
    ball.appendChild(swell);
    svg.appendChild(ball);
    dot.style.transformBox = 'fill-box';
    dot.style.transformOrigin = '50% 100%';
    swell.style.transformBox = 'fill-box';
    swell.style.transformOrigin = '50% 50%';

    // Path of the ball: drop onto the first letter, then parabolic hops.
    const at = (x: number, y: number) => `translate(${x}px, ${y - BALL_R}px)`;
    const frames: Keyframe[] = [];
    const total = DROP_MS + HOP_MS * (STOPS.length - 1);
    const first = STOPS[0];
    frames.push({ transform: at(first.x, first.y - 420), offset: 0, easing: 'cubic-bezier(.5,0,1,.6)' });
    frames.push({ transform: at(first.x, first.y), offset: DROP_MS / total });
    for (let s = 1; s < STOPS.length; s++) {
      const a = STOPS[s - 1];
      const b = STOPS[s];
      const t0 = DROP_MS + HOP_MS * (s - 1);
      const peak = Math.min(a.y, b.y) - (b.y > a.y + 20 ? 38 : 46);
      for (let k = 1; k <= 8; k++) {
        const u = k / 8;
        const x = a.x + (b.x - a.x) * u;
        // Quadratic arc through the peak (Bezier with the control point at the top).
        const c = 2 * peak - (a.y + b.y) / 2;
        const y = (1 - u) * (1 - u) * a.y + 2 * (1 - u) * u * c + u * u * b.y;
        frames.push({ transform: at(x, y), offset: (t0 + HOP_MS * u) / total });
      }
    }
    add(ball, frames, { duration: total, delay: DROP_AT });

    // Squash & stretch: the ball flattens on every landing, the letter dips under it.
    STOPS.forEach((st, i) => {
      const when = DROP_AT + DROP_MS + HOP_MS * i; // moment the ball touches this letter
      add(dot, [{ transform: 'scale(1,1)' }, { transform: 'scale(1.35,0.65)' }, { transform: 'scale(0.9,1.15)' }, { transform: 'scale(1,1)' }],
        { duration: 200, delay: when - 30, fill: 'none', easing: 'ease-out' });
      const p = letters[st.letter];
      if (p) add(p, [{ transform: 'scale(1,1)' }, { transform: 'scale(1.06,0.88)' }, { transform: 'scale(0.98,1.03)' }, { transform: 'scale(1,1)' }],
        { duration: 260, delay: when - 20, fill: 'none', easing: 'ease-out' });
    });

    // Finale: the ball swells to fill the screen, then the splash fades into the app.
    const endAt = DROP_AT + total + 180;
    add(swell, [{ transform: 'scale(1)' }, { transform: 'scale(1.3)', offset: 0.2 }, { transform: 'scale(110)' }],
      { duration: 620, delay: endAt, easing: 'cubic-bezier(.7,0,.6,1)' });
    add(host.closest('.intro-splash') as Element, [{ opacity: 1 }, { opacity: 1, offset: 0.7 }, { opacity: 0 }],
      { duration: DURATION_MS - endAt, delay: endAt });

    // Re-mount mid-way (e.g. right after sign-in): continue, don't restart.
    const elapsed = Math.max(0, Date.now() - startedAt);
    if (elapsed > 50) anims.forEach((a) => { a.currentTime = elapsed; });
    return () => anims.forEach((a) => a.cancel());
    // Built once per mount; startedAt only changes to 0 (unmount).
  }, [startedAt, reduce]);

  if (!startedAt) return null;
  // First name for the note: given with the queue (logout), else the signed-in user.
  const first = (note.name ?? user?.fullName ?? '').trim().split(/\s+/)[0] ?? '';
  const goodbye = note.kind === 'goodbye';
  return (
    <div className="intro-splash" onClick={() => { writeStart(0); writeNote(null); setStartedAt(0); }} aria-hidden="true">
      <div className="intro-stack">
        <div ref={hostRef} className="intro-logo-host" />
        <div className="intro-note">
          <div className="intro-note-title">{goodbye ? `Goodbye${first ? `, ${first}` : ''}!` : `Welcome${first ? `, ${first}` : ''}!`}</div>
          <div className="intro-note-sub">{goodbye ? 'See you soon' : 'Good to have you back'}</div>
        </div>
      </div>
    </div>
  );
}
